import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createApiUrl } from '../../config/api';
import './support.css';

const TicketStatus = () => {
  const { t } = useTranslation();
  const [ticketId, setTicketId] = useState('');
  const [email, setEmail] = useState('');
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for ticket ID in URL parameters
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ticketIdParam = urlParams.get('ticketId');
    const tokenParam = urlParams.get('t');
    if (ticketIdParam) {
      setTicketId(ticketIdParam);
      // Emailed links carry a signed token, so they open the ticket directly.
      // Older links without one fall back to asking for the email address.
      if (tokenParam) {
        setTimeout(() => {
          handleSearchTicket(ticketIdParam, tokenParam);
        }, 100);
      }
    }
  }, []);

  const handleSearchTicket = async (searchId = null, token = null) => {
    const idToSearch = String(
      (typeof searchId === 'string' || typeof searchId === 'number') ? searchId : ticketId
    ).trim().replace(/^#/, '');
    if (!idToSearch) {
      setError(t('supportPortal.status.idRequired'));
      return;
    }
    if (!token && !email.trim()) {
      setError(t('supportPortal.status.emailRequired'));
      return;
    }

    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (token) params.set('t', token);
      else params.set('email', email.trim());
      const response = await fetch(createApiUrl(
        `api/tickets/public/status/${encodeURIComponent(idToSearch)}?${params.toString()}`
      ));
      const data = await response.json();

      if (response.ok) {
        setTicketData(data.ticket);
      } else {
        setError(response.status === 404 ? t('supportPortal.status.notFound') : (data.error || t('supportPortal.status.notFound')));
        setTicketData(null);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setTicketData(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return '#f59e0b'; // Open - amber
      case 2: return '#10b981'; // Resolved - green
      default: return '#6b7280'; // Unknown - gray
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return 'Open';
      case 2: return 'Resolved';
      default: return 'Unknown';
    }
  };

  const getPriorityColor = (priorityName) => {
    switch (priorityName?.toLowerCase()) {
      case 'low': return '#10b981';
      case 'normal': return '#3b82f6';
      case 'high': return '#f59e0b';
      case 'urgent': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="support-container">
      {/* Header */}
      <div className="support-header">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ 
                width: '48px', 
                height: '48px', 
                background: 'white', 
                borderRadius: '12px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                marginRight: '1rem',
                color: '#000062',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>
                🔍
              </div>
              <div>
                <h1>Paramount Landscaping</h1>
                <h2>Check Ticket Status</h2>
              </div>
            </div>
            <a 
              href="/support" 
              style={{ 
                background: 'white', 
                color: '#000062', 
                padding: '0.75rem 1.5rem', 
                borderRadius: '8px', 
                textDecoration: 'none', 
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.background = '#f8fafc'}
              onMouseOut={(e) => e.target.style.background = 'white'}
            >
              ← Back to Support
            </a>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem' }}>
        {/* Search Section */}
        <div className="form-card" style={{ marginBottom: '2rem' }}>
          <div className="form-header">
            <h2 className="form-title">
              <div className="form-icon">🔍</div>
              Check Your Ticket Status
            </h2>
          </div>

          <div className="form-content">
            <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
              {t('supportPortal.status.instructions')}
            </p>
            
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                className="form-input"
                placeholder={t('supportPortal.status.ticketPlaceholder')}
                value={ticketId}
                onChange={(e) => setTicketId(e.target.value)}
                style={{ flex: '1 1 160px' }}
              />
              <input
                type="email"
                className="form-input"
                placeholder={t('supportPortal.status.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ flex: '2 1 220px' }}
              />
              <button
                className="submit-button"
                onClick={() => handleSearchTicket()}
                disabled={loading}
                style={{ minWidth: '120px' }}
              >
                {loading ? '⏳ Searching...' : '🔍 Search'}
              </button>
            </div>

            {error && (
              <div className="alert alert-error">
                ❌ {error}
              </div>
            )}
          </div>
        </div>

        {/* Ticket Details */}
        {ticketData && (
          <div className="form-card">
            <div className="form-header">
              <h2 className="form-title">
                <div className="form-icon">📋</div>
                Ticket #{ticketData.id}: {ticketData.name}
              </h2>
            </div>

            <div className="form-content">
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start', 
                marginBottom: '2rem',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div>
                  <p style={{ color: '#6b7280', margin: '0.5rem 0' }}>
                    Created on {formatDateTime(ticketData.created_date)}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{
                    background: getStatusColor(ticketData.status),
                    color: 'white',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '20px',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}>
                    {getStatusText(ticketData.status)}
                  </span>
                  {ticketData.priority_name && (
                    <span style={{
                      background: getPriorityColor(ticketData.priority_name),
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      {ticketData.priority_name}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '2rem',
                marginBottom: '2rem'
              }}>
                {/* Ticket Information */}
                <div>
                  <h3 style={{ color: '#000062', marginBottom: '1rem', fontSize: '1.125rem' }}>
                    Ticket Information
                  </h3>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                        Customer
                      </div>
                      <div style={{ fontWeight: '600', color: '#000062' }}>
                        {ticketData.customer_name}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                        Email
                      </div>
                      <div style={{ color: '#000062' }}>
                        {ticketData.email_address}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                        Category
                      </div>
                      <div style={{ color: '#000062' }}>
                        {ticketData.category_name}
                      </div>
                    </div>

                    {ticketData.assigned_to_name && (
                      <div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                          Assigned To
                        </div>
                        <div style={{ color: '#000062' }}>
                          {ticketData.assigned_to_name}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Timeline */}
                <div>
                  <h3 style={{ color: '#000062', marginBottom: '1rem', fontSize: '1.125rem' }}>
                    Status Timeline
                  </h3>
                  
                  <div style={{ 
                    background: '#f8fafc', 
                    padding: '1.5rem', 
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '1rem'
                    }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        background: getStatusColor(ticketData.status),
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        marginRight: '1rem',
                        fontSize: '0.875rem'
                      }}>
                        ✓
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#000062' }}>
                          {ticketData.status === 1 ? 'Ticket Received' : 'Ticket Resolved'}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {formatDateTime(ticketData.created_date)}
                        </div>
                      </div>
                    </div>
                    
                    {ticketData.status === 1 && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        opacity: 0.7
                      }}>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          background: '#3b82f6',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          marginRight: '1rem',
                          fontSize: '0.875rem'
                        }}>
                          ⏳
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: '#000062' }}>
                            In Progress
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            Our team is working on your request
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Ticket Description */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ color: '#000062', marginBottom: '1rem', fontSize: '1.125rem' }}>
                  Description
                </h3>
                <div style={{ 
                  background: '#f8fafc', 
                  padding: '1.5rem', 
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.6'
                }}>
                  {ticketData.notes}
                </div>
              </div>

              {/* Next Steps */}
              <div style={{ 
                background: ticketData.status === 1 ? '#dbeafe' : '#d1fae5', 
                border: `1px solid ${ticketData.status === 1 ? '#93c5fd' : '#a7f3d0'}`,
                borderRadius: '8px',
                padding: '1.5rem'
              }}>
                <h4 style={{ 
                  color: ticketData.status === 1 ? '#1e40af' : '#065f46', 
                  marginBottom: '0.5rem',
                  fontSize: '1rem'
                }}>
                  What's Next?
                </h4>
                <p style={{ 
                  color: ticketData.status === 1 ? '#1e40af' : '#065f46', 
                  margin: 0,
                  fontSize: '0.875rem',
                  lineHeight: '1.5'
                }}>
                  {ticketData.status === 1 ? (
                    <>
                      Your ticket is currently being reviewed by our support team. 
                      We'll respond within 2 hours during business hours. 
                      You'll receive an email notification when there's an update.
                    </>
                  ) : (
                    <>
                      This ticket has been resolved. If you have any follow-up questions, 
                      please don't hesitate to submit a new ticket or contact us directly.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Help Section */}
        {!ticketData && !loading && (
          <div className="form-card">
            <div className="form-content" style={{ textAlign: 'center' }}>
              <h3 style={{ color: '#000062', marginBottom: '1rem' }}>
                Need Help Finding Your Ticket ID?
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
                Your ticket ID was sent to your email when you submitted your request. 
                Check your inbox for an email from Paramount Landscaping.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a 
                  href="/support" 
                  className="submit-button"
                  style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  📝 Submit New Ticket
                </a>
                <a 
                  href="mailto:support@paramountlandscaping.com"
                  className="submit-button"
                  style={{ 
                    textDecoration: 'none', 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    background: '#6b7280'
                  }}
                >
                  ✉️ Contact Support
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketStatus;